import { LangFields } from "../../locale/strings";
import { SETUP_VIDEO } from "../onboard/setup/setupFilms";

/**
 * The help videos, one per question, each in the languages it was made in. They are on Cloudflare Stream and play
 * from videodelivery.net, the host the dashboard's frame-src and img-src allow (as for Replays and the setup video).
 * Chapter times and titles come from the films themselves; a placement starts a film at one of its chapters.
 */

export type FilmLanguage = "en" | "es" | "de";
export type FilmId = "setup" | "roles" | "jobs" | "settings" | "gps" | "invites" | "integrations";

/** One language's upload: the Stream id, its length and poster second, and where each chapter starts (seconds). */
type Cut = { uid: string; length: number; poster: number; chapters: number[] };

export type HelpFilm = {
    id: FilmId;
    cuts: Partial<Record<FilmLanguage, Cut>>;
    /** chapter titles in en, es, de, in the films' own words; the same order as each cut's chapters */
    chapters: LangFields[];
};

/** Where a film starts and ends for one place: chapter indexes, `to` excluded; no `to` plays to the end. */
export type FilmPart = { from: number; to?: number };

export const WHOLE: FilmPart = { from: 0 };

const FILMS: Record<FilmId, HelpFilm> = {
    setup: {
        id: "setup",
        cuts: { en: { uid: SETUP_VIDEO.uid, length: 174, poster: SETUP_VIDEO.frame, chapters: [0] } },
        chapters: [],
    },
    roles: {
        id: "roles",
        cuts: {
            en: { uid: "044032159a659367abb34bca1f72d24e", length: 94, poster: 33.5, chapters: [0, 16, 28, 53, 63, 72] },
            es: { uid: "6c882c4b21396ad79a9ad37c220f8925", length: 99, poster: 35.7, chapters: [0, 16, 29, 55, 66, 75] },
            de: { uid: "49847c484bf006a9ec4a198d69068b99", length: 98.5, poster: 36.2, chapters: [0, 16, 30, 56, 66, 75] },
        },
        chapters: [
            ["Three roles", "Tres roles", "Drei Rollen"],
            ["Administrator", "Administrador", "Administrator"],
            ["Operator", "Operador", "Disponent"],
            ["Worker", "Trabajador", "Mitarbeiter"],
            ["Many of each", "Varios de cada uno", "Von jeder Rolle mehrere"],
            ["Recap", "Resumen", "Zusammenfassung"],
        ],
    },
    jobs: {
        id: "jobs",
        cuts: {
            en: { uid: "696cb80475b98402f337db5479b4ea4a", length: 144.5, poster: 39.5, chapters: [0, 22, 55, 79, 110, 123] },
            es: { uid: "c35ee165adb31dab312692b52a7b1d95", length: 153.6, poster: 41.3, chapters: [0, 23, 57, 82, 116, 131] },
            de: { uid: "1ef18fbfffdaba0865faca356f658953", length: 155.8, poster: 41, chapters: [0, 23, 57, 84, 120, 134] },
        },
        chapters: [
            ["Intro", "Introducción", "Einführung"],
            ["Create a single job", "Crear una tarea", "Einen Auftrag anlegen"],
            ["Create a job from a template", "Crear una tarea con una plantilla", "Auftrag aus einer Vorlage"],
            ["Import jobs from a spreadsheet", "Importar tareas desde una hoja de cálculo", "Aufträge aus einer Tabelle importieren"],
            ["More ways to create jobs", "Más formas de crear tareas", "Weitere Wege"],
            ["Recap", "Resumen", "Zusammenfassung"],
        ],
    },
    settings: {
        id: "settings",
        cuts: {
            en: { uid: "cce73fb1676c15ea5fe6555644595fe7", length: 172, poster: 104, chapters: [0, 16, 30, 38, 59, 81, 95, 99, 128, 151] },
            es: { uid: "1f71f636a0931285cf4292f9cc5d92f8", length: 186.2, poster: 115.1, chapters: [0, 16, 31, 41, 63, 88, 104, 109, 140, 163] },
            de: { uid: "8a7b2d5b1d08f69952188a58c16aa10c", length: 191.9, poster: 116.6, chapters: [0, 16, 31, 40, 63, 90, 106, 111, 142, 167] },
        },
        chapters: [
            ["Intro", "Introducción", "Einführung"],
            ["Two ways into Company Settings", "Dos formas de entrar", "Zwei Wege in die Einstellungen"],
            ["General", "General", "Allgemein"],
            ["Location data", "Datos de ubicación", "Standortdaten"],
            ["Automatic check-ins", "Check-ins automáticos", "Automatische Check-ins"],
            ["Advanced mobile app settings", "Configuración avanzada de la app", "Erweiterte App-Einstellungen"],
            ["Notifications", "Notificaciones", "Benachrichtigungen"],
            ["Permissions for operators", "Permisos de los operadores", "Berechtigungen für Disponenten"],
            ["Permissions for workers", "Permisos de los trabajadores", "Berechtigungen für Mitarbeiter"],
            ["Wrap-up", "Cierre", "Abschluss"],
        ],
    },
    gps: {
        id: "gps",
        cuts: {
            en: { uid: "2511ebd1cb8138417109fdce6732b331", length: 184.4, poster: 64.8, chapters: [0, 18, 47, 87, 103, 128, 162] },
            es: { uid: "f56b8311005eb5fa3419b5f39db2826d", length: 211.2, poster: 73.3, chapters: [0, 19, 52, 98, 118, 148, 186] },
            de: { uid: "fa22d9658ef097322d61457177ae81e1", length: 198.9, poster: 68.9, chapters: [0, 20, 51, 93, 111, 138, 176] },
        },
        chapters: [
            ["Why tracks look off", "Por qué un recorrido se ve raro", "Warum Spuren seltsam aussehen"],
            ["Why GPS accuracy varies", "Por qué varía la precisión del GPS", "Warum die GPS-Genauigkeit schwankt"],
            ["Six common patterns", "Seis patrones comunes", "Sechs typische Muster"],
            ["Better accuracy", "Más precisión", "Mehr Genauigkeit"],
            ["Checklist on the phone", "Revisión en el teléfono", "Checkliste auf dem Handy"],
            ["Diagnose from the dashboard", "Diagnóstico desde el panel", "Diagnose im Dashboard"],
            ["Wrap-up", "Cierre", "Abschluss"],
        ],
    },
    invites: {
        id: "invites",
        cuts: {
            en: { uid: "9b9f44d900a2bf52b49461f5a5664e4e", length: 87.6, poster: 36.8, chapters: [0, 20, 30, 38, 56, 69] },
            es: { uid: "c49b0016418f1e21c18852ad29832255", length: 96.3, poster: 39.5, chapters: [0, 21, 32, 41, 61, 76] },
            de: { uid: "ced23898c318ca3a6603ddbefa9920cb", length: 94.6, poster: 38.6, chapters: [0, 20, 32, 40, 59, 75] },
        },
        chapters: [
            ["What invite codes are", "Qué son los códigos de invitación", "Was Einladungscodes sind"],
            ["Create a code", "Crear un código", "Einen Code erstellen"],
            ["Share it", "Compartirlo", "Teilen"],
            ["What your new team member does", "Qué hace la persona nueva", "Was die neue Person macht"],
            ["Delete a code", "Eliminar un código", "Einen Code löschen"],
            ["Recap", "Resumen", "Zusammenfassung"],
        ],
    },
    integrations: {
        id: "integrations",
        cuts: {
            en: { uid: "879649bc8f4c395f51b54722ccf7f655", length: 195.2, poster: 60.5, chapters: [0, 17, 47, 67, 99, 118, 131, 150, 174] },
            es: { uid: "13d187fb553d128753ec25ae8678880a", length: 220.2, poster: 68.6, chapters: [0, 19, 53, 75, 112, 134, 148, 170, 196] },
            de: { uid: "e74df56927c2454b75c44812edbdc31c", length: 223.7, poster: 68.7, chapters: [0, 18, 52, 75, 111, 133, 150, 174, 199] },
        },
        chapters: [
            ["What Hellotracks connects with", "Con qué se conecta Hellotracks", "Womit sich Hellotracks verbindet"],
            ["The lineup", "Las opciones", "Der Überblick"],
            ["Enable the API", "Habilitar la API", "Die API aktivieren"],
            ["Webhooks", "Webhooks", "Webhooks"],
            ["Connect Slack", "Conectar Slack", "Slack verbinden"],
            ["Alerts in Slack", "Alertas en Slack", "Alarme in Slack"],
            ["Google Calendar", "Google Calendar", "Google Kalender"],
            ["Email", "Correo", "E-Mail"],
            ["Wrap-up", "Cierre", "Abschluss"],
        ],
    },
};

/** The films on the Help page, in the order a new company meets the questions. */
export const HELP_PAGE_FILMS: FilmId[] = ["roles", "invites", "jobs", "settings", "gps", "integrations"];

const LANGUAGES: FilmLanguage[] = ["en", "es", "de"];
const PLAYER = "https://iframe.videodelivery.net/";
const THUMBNAILS = "https://videodelivery.net/";

export function helpFilm(id: FilmId): HelpFilm {
    return FILMS[id];
}

/** The languages a film was made in. */
export function filmLanguages(film: HelpFilm): FilmLanguage[] {
    return LANGUAGES.filter((language) => film.cuts[language]);
}

/** The dashboard's language when the film has it, else English. */
export function filmLanguage(film: HelpFilm, language: string | undefined): FilmLanguage {
    const short = (language || "en").slice(0, 2) as FilmLanguage;
    return film.cuts[short] ? short : "en";
}

export function filmCut(film: HelpFilm, language: FilmLanguage): Cut {
    return film.cuts[language] || film.cuts.en!;
}

/** A part's start and end in this language's cut, in seconds. */
export function partSeconds(film: HelpFilm, language: FilmLanguage, part: FilmPart): { start: number; end: number } {
    const cut = filmCut(film, language);
    const start = cut.chapters[part.from] ?? 0;
    const end = part.to !== undefined && cut.chapters[part.to] !== undefined ? cut.chapters[part.to] : cut.length;
    return { start, end };
}

export function chapterTitle(film: HelpFilm, index: number, language: FilmLanguage): string {
    const titles = film.chapters[index];
    return titles ? titles[LANGUAGES.indexOf(language)] : "";
}

/** "2:24", "0:57": whole seconds, cut off as Stream's player shows a length (144.5 s is 2:24 there). */
export function clock(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function filmThumbnailAt(film: HelpFilm, language: FilmLanguage, second: number, height: number) {
    const cut = filmCut(film, language);
    return `${THUMBNAILS}${cut.uid}/thumbnails/thumbnail.jpg?time=${Math.round(second)}s&height=${height}`;
}

/** Stream's player at a second, with the captions of the film's own language on. */
export function filmPlayerAt(film: HelpFilm, language: FilmLanguage, second: number) {
    const cut = filmCut(film, language);
    const track = film.id === "setup" ? "" : `&defaultTextTrack=${language}`;
    return `${PLAYER}${cut.uid}?autoplay=true&startTime=${Math.round(second)}s${track}`;
}
